import { StackedBarChartConfig } from "../../../../shared/components/stacked-bar-chart/stacked-bar-chart.model";
import {
  DashboardData,
  GlobalKpi,
  PlusDashboardData,
  PlusGlobalKpi,
  TimebasedKpi,
  TimePeriod,
} from "./../models/dashboard.models";
import { computed, effect, inject, Injectable, untracked } from "@angular/core";
import { StationPerformance } from "../models/station-performance.model";
import {
  ApiResponse,
  DataService,
  DataServiceInitialConfig,
  withMerchantDataService,
} from "apps/merchant-portal/src/modules/stations/services/with-merchant-data-service";
import { HttpParams } from "@angular/common/http";
import {
  ApiService,
  cleanHttpParamsInputObject,
} from "apps/merchant-portal/src/modules/shared";
import { delay, lastValueFrom, map } from "rxjs";
import { EntityId, withEntities } from "@ngrx/signals/entities";
import { withErCallState } from "apps/merchant-portal/src/modules/stations/services/with-er-call-state";
import { withBlockUiOnLoading } from "apps/merchant-portal/src/modules/stations/services/with-block-ui-on-loading";
import {
  patchState,
  signalStore,
  type,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from "@ngrx/signals";
import { SortOrder } from "@merchant-webapp/components";
import { downloadBlob } from "apps/merchant-portal/src/modules/shared/utils/form.util";
import { dashboardEndpointConfig } from "apps/merchant-portal/src/modules/shared/configs";
import {
  extractKPIS,
  extractSegment,
  getFuelVolumeOverviewPercents,
} from "apps/merchant-portal/src/modules/shared/utils/kpi.utils";
import {
  CHARTS_COLOR_SCHEME,
  HOVER_CHARTS_COLOR_SCHEME,
  PLUS_USER_CHARTS_COLOR_SCHEME,
} from "../constants/chart-color-scheme.constants";
import { DonutChartConfig } from "apps/merchant-portal/src/modules/shared/components/donut-chart/donut-chart.model";
import {
  DEFAULT_TIME_PERIOD_ID,
  i18CmpDirectionCompare,
  i18CmpFuelVolumeOverviewChartItems,
  i18CmpInvoicedTransactionItems,
  i18CmpPlusCustomerLocationItems,
  i18CmpProductItems,
  i18CmpTimePeriodDropdownOptions,
  i18CmpTotalPerformance,
} from "../dashboard.component";
import { withReset } from "apps/merchant-portal/src/modules/stations/services/with-reset";
import { FeatureFlagStore } from "apps/merchant-portal/src/modules/shared/services/feature-flag.service";
import { UserStore } from "apps/merchant-portal/src/modules/shared/stores";
import { mockGlobalKpi, mockTimebasedKpi } from "../dashboard.mock";

const allToggleColumns = ["dieselVolume", "petrolVolume", "adblueVolume"];

export const timePeriod: TimePeriod[] = [
  {
    id: "LAST_WEEK",
    name: `${i18CmpTimePeriodDropdownOptions}.last_week`,
    comparedWith: `${i18CmpDirectionCompare}.previous_week`,
  },
  {
    id: "LAST_MONTH",
    name: `${i18CmpTimePeriodDropdownOptions}.last_month`,
    comparedWith: `${i18CmpDirectionCompare}.previous_month`,
  },
  {
    id: "CURRENT_MONTH",
    name: `${i18CmpTimePeriodDropdownOptions}.current_month`,
    comparedWith: `${i18CmpDirectionCompare}.last_month`,
  },
  {
    id: "CURRENT_YEAR",
    name: `${i18CmpTimePeriodDropdownOptions}.current_year`,
    comparedWith: `${i18CmpDirectionCompare}.last_year`,
  },
];

export function mapExcludedFields(toggleColumns: string[]) {
  return allToggleColumns
    .filter((col) => !toggleColumns.includes(col))
    .map((col) => {
      switch (col) {
        case "dieselVolume":
          return "DIESEL";
        case "petrolVolume":
          return "PETROL";
        case "adblueVolume":
          return "ADBLUE";
      }
      return "SOMETHING_WENT_WRONG";
    });
}

@Injectable({ providedIn: "root" })
export class DashboardDataService implements DataService<StationPerformance> {
  private readonly apiService = inject(ApiService);
  private readonly userStore = inject(UserStore);
  private readonly featureFlagStore = inject(FeatureFlagStore);

  async downloadPerformanceFile(params: HttpParams) {
    const blob = await lastValueFrom(
      this.apiService.getFile<Blob>(
        "api/v1/dashboards/stations/download",
        params,
      ),
    );
    downloadBlob(blob, "performance");
  }

  hasPlusStations() {
    return this.userStore.isPlusStation();
  }

  getPlusGlobalData(period: string) {
    return this.apiService.get<PlusGlobalKpi>(
      dashboardEndpointConfig.dashboardGlobal(),
      new HttpParams().set("period", period),
    );
  }

  getGlobalData(period: string) {
    return this.apiService.get<GlobalKpi>(
      dashboardEndpointConfig.dashboardGlobal(),
      new HttpParams().set("period", period),
    );
  }

  getPlusTimebasedData(period: string) {
    return this.apiService.get<TimebasedKpi>(
      dashboardEndpointConfig.dashboardTimeBase(),
      new HttpParams().set("period", period),
    );
  }

  getTimebasedData(period: string) {
    return this.apiService.get<TimebasedKpi>(
      dashboardEndpointConfig.dashboardTimeBase(),
      new HttpParams().set("period", period),
    );
  }

  getDashboardData(period: string) {
    return this.apiService.get<DashboardData>(
      dashboardEndpointConfig.dashboardData(),
      new HttpParams().set("period", period),
    );
  }

  getPlusDashboardData(period: string) {
    return this.apiService.get<PlusDashboardData>(
      dashboardEndpointConfig.plusDashboardData(),
      new HttpParams().set("period", period),
    );
  }

  loadById(id: EntityId): Promise<StationPerformance> {
    throw new Error("Method not implemented.");
  }
  create(entity: StationPerformance): Promise<StationPerformance> {
    throw new Error("Method not implemented.");
  }
  update(entity: StationPerformance): Promise<StationPerformance> {
    throw new Error("Method not implemented.");
  }
  updateAll(entity: StationPerformance[]): Promise<StationPerformance[]> {
    throw new Error("Method not implemented.");
  }
  delete(entity: StationPerformance): Promise<void> {
    throw new Error("Method not implemented.");
  }
  load(params: HttpParams): Promise<ApiResponse<StationPerformance>> {
    if (
      params.get("sorting_direction") === undefined ||
      params.get("sorting_direction") === null
    ) {
      throw new Error(
        "TODO: sorting direction must have default value, this should never be reached",
      );
    }

    return lastValueFrom(
      this.apiService
        .get<
          ApiResponse<StationPerformance>
        >(this.featureFlagStore.dashboards_data_v2() ? dashboardEndpointConfig.dashboardStationsV2() : dashboardEndpointConfig.dashboardStationsV1(), params)
        .pipe(
          delay(1000),
          map((res) => {
            (res as any).content = (res as any).content.map((el: any) => {
              el.id = el.station_id;
              return el;
            });
            return res;
          }),
        ),
    );
  }
}

export const initialStationPerformanceParams: DataServiceInitialConfig<
  any,
  any
> = {
  sort: {
    sort_by: "dieselVolume",
    sorting_direction: SortOrder.desc,
  },
  filter: {
    period: "",
    station: "",
  } as { period: string; station: string },

  parentFilterName: "dashboardFilters",
};

export const DashboardStore = signalStore(
  { providedIn: "root" },
  withState({
    dashboardFilters: { period: DEFAULT_TIME_PERIOD_ID },

    plusGlobalKpi: {} as PlusGlobalKpi,
    plusInvoicedTransactionConfig: {} as StackedBarChartConfig,
    plusCustomerLocationConfig: {} as DonutChartConfig,

    globalKpi: {} as GlobalKpi,
    totalVolumesStackBarChartConfig: {} as StackedBarChartConfig,
    fuelVolumeOverviewDonutChartConfig: {} as DonutChartConfig,

    toggleColumns: allToggleColumns,
    alwaysDisplayedColumns: [
      "internalMerchantStationId",
      "externalStationId",
      "stationName",
      "stationAddress",
    ],
  }),
  withComputed(({ dashboardFilters: { period }, ...store }) => ({
    columns: computed(() => [
      ...store.alwaysDisplayedColumns(),
      ...store.toggleColumns(),
    ]),
    selectedTimePeriod: computed(() => {
      return timePeriod.find((p) => p.id === period()) ?? ({} as TimePeriod);
    }),
    explanationMessage: computed(
      () => `${i18CmpTotalPerformance}.explanation.${period().toLowerCase()}`,
    ),
  })),
  withErCallState({
    collection: "stationPerformance",
  }),
  withBlockUiOnLoading({
    collection: "stationPerformance",
  }),
  withEntities({
    entity: type<StationPerformance>(),
    collection: "stationPerformance",
  }),
  withMerchantDataService({
    dataServiceType: DashboardDataService,
    initialConfig: initialStationPerformanceParams,
    collection: "stationPerformance",
  }),
  withMethods((store, dashboardDataService = inject(DashboardDataService)) => ({
    mapTimebasedData(timebasedKpi: TimebasedKpi) {
      return {
        totalVolumesStackBarChartConfig: {
          items: [
            {
              label: `${i18CmpProductItems}.diesel`,
              value: "diesel_volume",
            },
            {
              label: `${i18CmpProductItems}.petrol`,
              value: "petrol_volume",
            },
            {
              label: `${i18CmpProductItems}.adblue`,
              value: "adblue_volume",
            },
          ],
          datasets: extractKPIS(timebasedKpi.data),
          labels: extractSegment(timebasedKpi.data, "time_segment"),
          colors: CHARTS_COLOR_SCHEME,
          measure: "",
          formatNumberYAxis: true,
        },
        fuelVolumeOverviewDonutChartConfig: {
          labels: [
            `${i18CmpFuelVolumeOverviewChartItems}.diesel`,
            `${i18CmpFuelVolumeOverviewChartItems}.petrol`,
            `${i18CmpFuelVolumeOverviewChartItems}.ad_blue`,
          ],
          colors: CHARTS_COLOR_SCHEME,
          hoverColors: HOVER_CHARTS_COLOR_SCHEME,
          data: getFuelVolumeOverviewPercents(timebasedKpi.data),
        },
      };
    },
    mapPlusTimebasedData: (timebasedKpi: TimebasedKpi) => {
      return {
        plusInvoicedTransactionConfig: {
          items: [
            {
              label: `${i18CmpInvoicedTransactionItems}.national_customers`,
              value: "national_customers",
            },
            {
              label: `${i18CmpInvoicedTransactionItems}.international_customers`,
              value: "international_customers",
            },
          ],
          datasets: extractKPIS(timebasedKpi.data),
          labels: extractSegment(timebasedKpi.data, "time_segment"),
          dates: extractSegment(timebasedKpi.data, "date"),
          colors: PLUS_USER_CHARTS_COLOR_SCHEME,
          measure: "",
          hideSelectType: true,
        },
        plusCustomerLocationConfig: {
          labels: [
            `${i18CmpPlusCustomerLocationItems}.national_customers`,
            `${i18CmpPlusCustomerLocationItems}.international_customers`,
          ],
          colors: PLUS_USER_CHARTS_COLOR_SCHEME,
          hoverColors: PLUS_USER_CHARTS_COLOR_SCHEME,
          data: getFuelVolumeOverviewPercents(timebasedKpi.data),
        },
      };
    },
    mapDashboardData(globalKpi: GlobalKpi, timebasedKpi: TimebasedKpi) {
      return {
        globalKpi: {
          ...globalKpi,
          sumVolume: Object?.values(globalKpi?.volume || {})
            .filter((value) => typeof value === "number")
            .reduce((acc, num) => acc + num, 0),
        },
        ...this.mapTimebasedData(timebasedKpi),
      };
    },
    mapPlusDashboardData(globalKpi: PlusGlobalKpi, timebasedKpi: TimebasedKpi) {
      return {
        plusGlobalKpi: globalKpi,
        ...this.mapPlusTimebasedData(timebasedKpi),
      };
    },
    getDashboardData(period: string) {
      if (dashboardDataService.hasPlusStations()) {
        dashboardDataService
          .getPlusDashboardData(period)
          .subscribe(({ global_kpi, timebased_kpi }) => {
            patchState(
              store,
              this.mapPlusDashboardData(mockGlobalKpi, mockTimebasedKpi),
            );
          });
      } else {
        dashboardDataService
          .getDashboardData(period)
          .subscribe(({ global_kpi, timebased_kpi }) => {
            patchState(store, this.mapDashboardData(global_kpi, timebased_kpi));
          });
      }
    },
    getTimebasedData(period: string) {
      if (dashboardDataService.hasPlusStations()) {
        dashboardDataService
          .getPlusTimebasedData(period)
          .subscribe((timebasedKpi): void => {
            patchState(store, {
              ...store.plusGlobalKpi,
              ...this.mapPlusTimebasedData(mockTimebasedKpi),
            });
          });
      } else {
        dashboardDataService
          .getTimebasedData(period)
          .subscribe((timebasedKpi) => {
            patchState(store, {
              ...store.globalKpi,
              ...this.mapTimebasedData(timebasedKpi),
            });
          });
      }
    },
    getGlobalData: (period: string) => {
      if (dashboardDataService.hasPlusStations()) {
        dashboardDataService
          .getPlusGlobalData(period)
          .subscribe((globalKpi) => {
            patchState(store, {
              ...store.plusInvoicedTransactionConfig,
              ...store.plusCustomerLocationConfig,
              plusGlobalKpi: mockGlobalKpi,
            });
          });
      } else {
        dashboardDataService.getGlobalData(period).subscribe((globalKpi) => {
          patchState(store, {
            ...store.totalVolumesStackBarChartConfig,
            ...store.fuelVolumeOverviewDonutChartConfig,
            globalKpi: {
              ...globalKpi,
              sumVolume: Object?.values(globalKpi?.volume || {})
                .filter((value) => typeof value === "number")
                .reduce((acc, num) => acc + num, 0),
            },
          });
        });
      }
    },
    setDisplayedColumns: (toggleColumns: string[]) => {
      patchState(store, () => ({ toggleColumns }));
    },
    setTimePeriod: (period: string) => {
      patchState(store, { dashboardFilters: { period } });
    },
    downloadPerformanceFile: async () => {
      const params = new HttpParams({
        fromObject: cleanHttpParamsInputObject({
          ...store.stationPerformanceFilter(),
          excluded_fields_from_excel: mapExcludedFields(store.toggleColumns()),
          page_size: store.stationPerformancePagination.page_size(),
          page_index: store.stationPerformancePagination.page_index(),
          ...store.stationPerformanceSorting(),
        }),
      });

      return await dashboardDataService.downloadPerformanceFile(params);
    },
  })),
  withHooks({
    onInit: (store) => {
      const featureFlagStore = inject(FeatureFlagStore);

      effect(() => {
        const { period } = store.dashboardFilters();
        const isEnabledDasboardsDataV2 = featureFlagStore.dashboards_data_v2();

        untracked(() => {
          if (isEnabledDasboardsDataV2) {
            store.getGlobalData(period);
            store.getTimebasedData(period);
          } else {
            store.getDashboardData(period);
          }
        });
      });
    },
  }),
  withReset(),
);
